// src/pages/Settings.jsx
import React, { useEffect, useState, useContext, useRef } from "react";
import API from "../api";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { usePageTitle } from "../hooks/usePageTitle";
import { useSearchParams, useNavigate } from "react-router-dom";
import ConfirmModal from "../components/ConfirmModal";
import { UserContext } from "../context/UserContext";
import {
  COMPETITION_OPTIONS,
  getEnabledCompetitionIds,
  getStoredPreferredCompetitionId,
  setStoredPreferredCompetitionId,
} from "../constants/competitionPrefs";

export default function Settings() {
  usePageTitle('Settings');
  
  const [oldPass, setOldPass] = useState("");
  const [newPass, setNewPass] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [prefs, setPrefs] = useState({
    notify_weekly: true,
    notify_unfinished: false,
  });
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [searchParams] = useSearchParams();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const { user, setUser } = useContext(UserContext); // Need setUser for logout
  const navigate = useNavigate();
  const [preferredLeague, setPreferredLeague] = useState(() =>
    getStoredPreferredCompetitionId()
  );
  const [enabledCompetitions, setEnabledCompetitions] = useState(() =>
    getEnabledCompetitionIds(user)
  );
  const [savingCompetitionPrefs, setSavingCompetitionPrefs] = useState(false);
  const [usernameDraft, setUsernameDraft] = useState("");
  const [savingUsername, setSavingUsername] = useState(false);
  const [usernameCheck, setUsernameCheck] = useState({ status: "idle" });
  const usernameDebounceRef = useRef(null);

  useEffect(() => {
    if (user) {
      setEnabledCompetitions(getEnabledCompetitionIds(user));
    }
  }, [user?.id, user?.enabled_competitions]);

  const handleToggleCompetition = (id) => {
    setEnabledCompetitions((prev) => {
      if (prev.includes(id)) {
        if (prev.length <= 1) {
          toast.warning("Keep at least one league enabled.");
          return prev;
        }
        return prev.filter((x) => x !== id);
      }
      return [...prev, id];
    });
  };

  const handleSaveCompetitionPrefs = async () => {
    if (!user || enabledCompetitions.length === 0) {
      toast.error("Select at least one league.");
      return;
    }
    setSavingCompetitionPrefs(true);
    try {
      const { data } = await API.patch("/users/me/competition-prefs", {
        enabled_competitions: enabledCompetitions,
      });
      const saved = data.enabled_competitions || enabledCompetitions;
      setEnabledCompetitions(saved);
      setUser((prev) => (prev ? { ...prev, enabled_competitions: saved } : prev));
      if (!saved.includes(preferredLeague)) {
        const next = saved.includes("premier_league") ? "premier_league" : saved[0];
        setPreferredLeague(next);
        setStoredPreferredCompetitionId(next);
      }
      toast.success("League preferences saved.");
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not save league preferences.");
    } finally {
      setSavingCompetitionPrefs(false);
    }
  };


  useEffect(() => {
    if (user?.id != null) setUsernameDraft(user.username ?? "");
  }, [user?.id]);

  useEffect(() => {
    if (!user) return;
    const trimmed = usernameDraft.trim();
    const current = (user.username || "").trim();
    if (trimmed === current) {
      setUsernameCheck({ status: "idle" });
      return;
    }
    if (trimmed.length < 3) {
      setUsernameCheck({
        status: "invalid",
        message: "Use 3–20 characters.",
      });
      return;
    }
    setUsernameCheck({ status: "checking" });
    if (usernameDebounceRef.current) clearTimeout(usernameDebounceRef.current);
    usernameDebounceRef.current = setTimeout(async () => {
      try {
        const { data } = await API.get("/users/check-username", {
          params: { username: trimmed },
        });
        setUsernameCheck(
          data.available
            ? { status: "available" }
            : { status: "taken", message: data.message || "Taken" }
        );
      } catch {
        setUsernameCheck({ status: "idle" });
      }
    }, 400);
    return () => {
      if (usernameDebounceRef.current) clearTimeout(usernameDebounceRef.current);
    };
  }, [usernameDraft, user]);

  const handleSaveUsername = async () => {
    if (!user) return;
    const trimmed = usernameDraft.trim();
    const current = (user.username || "").trim();
    if (trimmed === current) {
      toast.info("No changes to save.");
      return;
    }
    if (usernameCheck.status === "taken" || usernameCheck.status === "invalid") {
      toast.error("Choose a valid, available username before saving.");
      return;
    }
    setSavingUsername(true);
    try {
      const { data } = await API.patch("/users/me", { username: trimmed });
      setUser((prev) => (prev ? { ...prev, username: data.username } : prev));
      setUsernameDraft(data.username);
      toast.success("Username saved.");
      setUsernameCheck({ status: "idle" });
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not save username.");
    } finally {
      setSavingUsername(false);
    }
  };

  // Logout Logic
  const handleLogout = () => {
    localStorage.removeItem("token");
    setUser(null);
    navigate("/auth", { replace: true });
  };

  // Load email prefs + handle unsubscribe link
  useEffect(() => {
    (async () => {
      try {
        const token = searchParams.get("unsub");
        if (token) {
          await API.post("/notifications/unsubscribe", { token, type: "all" });
          toast.success("✅ You have been unsubscribed from all emails.", { autoClose: 2500 });
        }
      } catch (e) {
        // ignore token errors to not block page
      }
      try {
        const { data } = await API.get("/settings/email-prefs");
        setPrefs(data);
      } catch (e) {
        // not fatal
      }
    })();
  }, [searchParams]);

  const handleChangePassword = async () => {
    if (!oldPass || !newPass || !confirmPass) {
      toast.error("Please fill in all password fields.");
      return;
    }
    if (newPass !== confirmPass) {
      toast.error("New passwords do not match.");
      return;
    }
    if (newPass.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    setIsChangingPassword(true);
    try {
      await API.put("/users/change-password", {
        oldPassword: oldPass,
        newPassword: newPass,
      });
      toast.success("✅ Password changed successfully.");
      setOldPass("");
      setNewPass("");
      setConfirmPass("");
    } catch (err) {
      toast.error(err.response?.data?.message || "❌ Change password failed.");
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    try {
      await API.delete("/users/delete-account");
      toast.success("🗑️ Account deleted. Redirecting...");
      setTimeout(() => (window.location.href = "/"), 2000);
    } catch {
      toast.error("❌ Delete account failed.");
      setIsDeleting(false);
    }
  };

  const handleSavePrefs = async () => {
    setSavingPrefs(true);
    try {
      const { data } = await API.put("/settings/email-prefs", {
        notify_weekly: prefs.notify_weekly,
        notify_unfinished: !!prefs.notify_unfinished,
        notify_open: false,
        notify_24h: false,
        notify_1h: false,
      });
      setPrefs(data);
      toast.success("✅ Email preferences saved.");
    } catch (err) {
      toast.error(err.response?.data?.message || "❌ Could not save preferences.");
    } finally {
      setSavingPrefs(false);
    }
  };

  const handleUnsubscribeAll = async () => {
    setSavingPrefs(true);
    try {
      const { data } = await API.put("/settings/email-prefs", {
        unsubscribe_all: true,
      });
      setPrefs(data);
      toast.success("✅ Unsubscribed from all emails.");
    } catch (err) {
      toast.error("❌ Could not unsubscribe.");
    } finally {
      setSavingPrefs(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper text-ink">
      <main className="max-w-7xl mx-auto px-4 md:px-6 pt-5 md:pt-8 pb-8 space-y-6">
        <div>
          <h1 className="text-[22px] md:text-[28px] font-bold text-ink">Settings</h1>
          <p className="mt-1 text-meta text-muted">
            Password, notifications, and which leagues you play.
          </p>
        </div>
        {/* Account email summary */}
        {user?.email && (
          <section className="bg-white rounded-xl border border-gray-200 p-6 md:p-8 shadow-sm flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <h2 className="text-[17px] font-semibold text-ink">
                Account Email
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                This is the email linked to your account and used for login and notifications.
              </p>
            </div>
            <div className="px-3 py-1.5 rounded-full bg-brand text-white text-sm font-mono">
              {user.email}
            </div>
          </section>
        )}

        {user && (
          <section className="bg-white rounded-xl border border-gray-200 p-6 md:p-8 shadow-sm">
            <h2 className="text-[17px] font-semibold text-ink">
              Username
            </h2>
            <p className="text-sm text-gray-600 mt-1 mb-4">
              This name appears on leaderboards and your profile. You can change it anytime
              (works for email login and Google sign-in).
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:items-end max-w-xl">
              <div className="flex-1 min-w-0">
                <label htmlFor="settings-username" className="sr-only">
                  Username
                </label>
                <input
                  id="settings-username"
                  type="text"
                  autoComplete="username"
                  value={usernameDraft}
                  onChange={(e) => setUsernameDraft(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-900 font-mono focus:border-gray-900 focus:ring-1 focus:ring-gray-900"
                  maxLength={20}
                />
                <p className="text-xs text-gray-500 mt-1.5">
                  3–20 characters: letters, numbers, underscores, hyphens.
                </p>
                {usernameCheck.status === "checking" && (
                  <p className="text-xs text-gray-500 mt-1">Checking availability…</p>
                )}
                {usernameCheck.status === "available" && (
                  <p className="text-xs text-green-700 mt-1">Available</p>
                )}
                {usernameCheck.status === "taken" && (
                  <p className="text-xs text-red-600 mt-1">
                    {usernameCheck.message || "Username is already taken."}
                  </p>
                )}
                {usernameCheck.status === "invalid" && (
                  <p className="text-xs text-amber-700 mt-1">
                    {usernameCheck.message}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={handleSaveUsername}
                disabled={
                  savingUsername ||
                  usernameDraft.trim() === (user.username || "").trim() ||
                  usernameCheck.status === "taken" ||
                  usernameCheck.status === "invalid"
                }
                className={`shrink-0 px-4 py-2 rounded-lg font-semibold text-sm transition-colors ${
                  savingUsername ||
                  usernameDraft.trim() === (user.username || "").trim() ||
                  usernameCheck.status === "taken" ||
                  usernameCheck.status === "invalid"
                    ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                    : "bg-brand text-white hover:bg-brand-hover"
                }`}
              >
                {savingUsername ? "Saving…" : "Save username"}
              </button>
            </div>
          </section>
        )}

        <section className="bg-white rounded-xl border border-gray-200 p-6 md:p-8 shadow-sm space-y-5">
          <div>
            <h2 className="text-[17px] font-semibold text-ink">
              Leagues I play
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Choose which competitions appear in Predictions, Fixtures, and Leaderboard.
              Synced to your account on all devices.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {COMPETITION_OPTIONS.map((c) => (
              <label
                key={c.id}
                className="flex items-center gap-3 rounded-lg border border-gray-200 px-4 py-3 cursor-pointer hover:bg-gray-50"
              >
                <input
                  type="checkbox"
                  checked={enabledCompetitions.includes(c.id)}
                  onChange={() => handleToggleCompetition(c.id)}
                  className="h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900"
                />
                <span className="text-sm font-medium text-gray-900">{c.label}</span>
              </label>
            ))}
          </div>
          <button
            type="button"
            onClick={handleSaveCompetitionPrefs}
            disabled={savingCompetitionPrefs}
            className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
              savingCompetitionPrefs
                ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                : "bg-brand text-white hover:bg-brand-hover"
            }`}
          >
            {savingCompetitionPrefs ? "Saving…" : "Save league preferences"}
          </button>

          <div className="pt-4 border-t border-gray-100">
            <h3 className="text-sm font-semibold text-gray-900 mb-1">
              Default league (Predictions)
            </h3>
            <p className="text-sm text-gray-600 mb-3">
              Which enabled competition opens first on this device.
            </p>
            <select
              value={preferredLeague}
              onChange={(e) => {
                const id = e.target.value;
                setPreferredLeague(id);
                setStoredPreferredCompetitionId(id);
                toast.success("Default league saved.");
              }}
              className="w-full max-w-md px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-900 focus:border-gray-900 focus:ring-1 focus:ring-gray-900"
            >
              {COMPETITION_OPTIONS.filter((c) => enabledCompetitions.includes(c.id)).map(
                (c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                )
              )}
            </select>
          </div>
        </section>
        
        {/* Logout Button (Visible on Mobile especially) */}
        <button
          onClick={handleLogout}
          className="w-full md:hidden px-4 py-3 bg-red-50 text-red-700 border border-red-200 rounded-xl font-semibold hover:bg-red-100 transition-colors mb-4"
        >
          Log Out
        </button>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Notifications */}
          <section className="bg-white rounded-xl border border-gray-200 p-6 md:p-8 shadow-sm">
            <h2 className="text-[17px] font-semibold text-ink mb-1">Notifications</h2>
            <p className="text-sm text-gray-600 mb-6">
              One weekly email and two scheduled Discord posts for the league — no per-matchday spam.
            </p>

            <div className="mb-6 pb-6 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Email</h3>
              <label className="flex items-center justify-between text-sm">
                <span>
                  Weekly roundup
                  <span className="block text-xs text-gray-500 mt-0.5">
                    Mondays · 10:00 UTC (3:30 PM IST) · your scores, rank, and fixtures for enabled leagues
                  </span>
                </span>
                <input
                  type="checkbox"
                  checked={!!prefs.notify_weekly}
                  onChange={(e) => setPrefs({ ...prefs, notify_weekly: e.target.checked })}
                  className="ml-4 shrink-0"
                />
              </label>
            </div>

            <div className="mb-6 pb-6 border-b border-gray-100">
              <label className="flex items-center justify-between text-sm">
                <span>
                  Unfinished picks
                  <span className="block text-xs text-gray-500 mt-0.5">
                    A line in the Monday email when matches are still open. Off unless you turn it on. No separate email, and nothing about streaks.
                  </span>
                </span>
                <input
                  type="checkbox"
                  checked={!!prefs.notify_unfinished}
                  onChange={(e) => setPrefs({ ...prefs, notify_unfinished: e.target.checked })}
                  className="ml-4 shrink-0"
                />
              </label>
              {!prefs.notify_weekly && (
                <p className="text-xs text-gray-500 mt-2">Turn on the weekly roundup to receive this.</p>
              )}
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Discord</h3>
              <p className="text-sm text-gray-600">
                Server-wide alerts in the Predict90 Discord — not controlled here.
              </p>
              <ul className="mt-3 space-y-2 text-sm text-gray-600 list-disc list-inside">
                <li>
                  <strong>Monday</strong> — week ahead, top 3 per league, @Prediction League
                </li>
                <li>
                  <strong>Friday</strong> — weekend fixtures (Fri–Mon), @Prediction League
                </li>
              </ul>
            </div>

            <p className="text-xs text-gray-500 mt-4">
              Individual matches lock 30 minutes before kick-off.
            </p>
            <div className="flex gap-2 mt-6">
              <button
                onClick={handleSavePrefs}
                disabled={savingPrefs}
                className={`px-4 py-2 bg-brand hover:bg-brand-hover text-white rounded-md font-semibold transition ${
                  savingPrefs ? "opacity-70 cursor-not-allowed" : ""
                }`}
              >
                Save Preferences
              </button>
              <button
                onClick={handleUnsubscribeAll}
                disabled={savingPrefs}
                className={`px-4 py-2 border border-gray-900 text-gray-900 rounded-md font-semibold transition hover:bg-gray-100 ${
                  savingPrefs ? "opacity-70 cursor-not-allowed" : ""
                }`}
              >
                Unsubscribe All
              </button>
            </div>
          </section>

          {/* Password & Danger */}
          <section className="space-y-8">
            <div className="bg-white rounded-xl border border-gray-200 p-6 md:p-8 shadow-sm">
              <h2 className="text-[17px] font-semibold text-ink mb-1">Change password</h2>
              <p className="text-sm text-gray-600 mb-6">
                Use a strong unique password to keep your account secure.
              </p>
              <div className="space-y-3">
          <input
            type="password"
            placeholder="Old Password"
            value={oldPass}
            onChange={(e) => setOldPass(e.target.value)}
                  className="w-full px-4 py-2 rounded-md bg-gray-50 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <input
            type="password"
            placeholder="New Password"
            value={newPass}
            onChange={(e) => setNewPass(e.target.value)}
                  className="w-full px-4 py-2 rounded-md bg-gray-50 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="password"
                  placeholder="Confirm New Password"
                  value={confirmPass}
                  onChange={(e) => setConfirmPass(e.target.value)}
                  className="w-full px-4 py-2 rounded-md bg-gray-50 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
              </div>
          <button
            onClick={handleChangePassword}
                disabled={
                  isChangingPassword ||
                  !oldPass ||
                  !newPass ||
                  !confirmPass
                }
                className={`mt-4 w-full bg-brand hover:bg-brand-hover text-white py-2 rounded-md font-semibold transition ${
                  isChangingPassword || !oldPass || !newPass || !confirmPass
                    ? "opacity-70 cursor-not-allowed"
                    : ""
                }`}
              >
                {isChangingPassword ? "Updating..." : "Update Password"}
          </button>
        </div>

            <div className="bg-white rounded-xl border border-gray-200 p-6 md:p-8 shadow-sm">
              <h3 className="text-xl font-semibold text-red-600 mb-2">Delete Account</h3>
              <p className="text-sm text-gray-700 mb-4">
            This action is <strong>irreversible</strong>. All your data will be permanently deleted.
          </p>
          <button
                onClick={() => {
                  setDeleteConfirmText("");
                  setShowDeleteConfirm(true);
                }}
            disabled={isDeleting}
                className={`w-full py-2 rounded-md font-semibold transition ${
              isDeleting
                ? "bg-gray-400 text-gray-700 cursor-not-allowed"
                : "bg-red-600 hover:bg-red-700 text-white"
            }`}
          >
            {isDeleting ? "Deleting…" : "Delete My Account"}
          </button>
        </div>
          </section>
        </div>
        <ToastContainer position="top-center" />
      </main>
      <ConfirmModal
        isOpen={showDeleteConfirm}
        title="Delete your account?"
        message={
          <div>
            <p className="mb-2">
              This will permanently delete your account, predictions, groups and
              scores.
            </p>
            <p className="text-sm text-gray-600">
              Type <span className="font-mono font-semibold">DELETE</span> to
              confirm.
            </p>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="Type DELETE to confirm"
              className="mt-2 w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
            />
      </div>
        }
        confirmText={isDeleting ? "Deleting…" : "Delete permanently"}
        confirmColor="red"
        onCancel={() => {
          if (isDeleting) return;
          setShowDeleteConfirm(false);
        }}
        onConfirm={async () => {
          if (deleteConfirmText !== "DELETE") {
            toast.error('Please type "DELETE" to confirm.');
            return;
          }
          setShowDeleteConfirm(false);
          await handleDeleteAccount();
        }}
      />
    </div>
  );
}
